<script setup lang="ts">

import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {
    Send,
    ArrowRight,
    GitCommitVertical,
    Pill,
    History,
    Gift,
    PartyPopper,
    ChevronRight,
    SendHorizonal
} from "@lucide/vue";
import ApodisPremiumCreditUpsellBanner
    from "@/subscription-plan/components/apodis-premium/ApodisPremiumCreditUpsellBanner.vue";
import {storeToRefs} from "pinia";
import {useCreditStore} from "@/stores/credit.store";
import BuyCreditsButton from "@/communication/components/buttons/BuyCreditsButton.vue";
import UpcomingSequenceMessagesTimeline from "@/rcs/components/scheduled/UpcomingSequenceMessagesTimeline.vue";

const {credit} = storeToRefs(useCreditStore());


const baseCards = [
    {
        title: "Envoi manuel",
        description: "Sélectionnez vous-même vos patients et envoyez-leur un message ponctuel - SMS, RCS ou notification",
        border: 'hover:border-primary',
        icon: {
            component: Send,
            background: 'bg-lightPurple-lightest text-primary'
        },
        routeName: 'patient-administration.communication.customers',
        footer: {
            text: "Composer un message",
            class: "text-primary"
        }
    },
    {
        title: "Depuis un programme",
        border: 'border-primary',
        description: "Ciblez la patientèle d'un programme (diabète, vaccination, fidélité...) et communiquez avec elle.",
        icon: {
            component: Pill,
            background: 'bg-lightPurple-lightest text-primary'
        },
        routeName: 'patient-administration.communication.programs',
        footer: {
            text: "Choisir un programme",
            class: "text-primary"
        }
    },
    {
        title: "Séquence automatisée",
        border: 'border-primary',
        description: "Automatisez l'envoi : quand un patient achète un produit défini, il reçoit un message automatiquement.",
        icon: {
            component: GitCommitVertical,
            background: 'bg-lightPurple-lightest text-primary'
        },
        routeName: 'patient-administration.communication.sequences',
        footer: {
            text: "Gérer les séquences",
            class: "text-primary"
        }
    },
    {
        title: "Historique des envois",
        border: 'border-primary',
        description: "Retrouvez l'ensemble des messages envoyés à vos patients et suivez leur statut.",
        icon: {
            component: History,
            background: 'bg-lightPurple-lightest text-primary'
        },
        routeName: 'patient-administration.communication.conversation-messages',
        footer: {
            text: "Voir l'historique",
            class: "text-primary"
        }
    }
];

const birthdayGiftCard = {
    title: "Cadeaux d'anniversaire",
    border: 'border-primary',
    description: "Envoyez un message RCS le jour de l'anniversaire de vos patients fidèles : un cadeau surprise à retirer au comptoir avec un QR code.",
    icon: {
        component: Gift,
        background: 'bg-lightPurple-lightest text-primary'
    },
    routeName: 'patient-administration.communication.birthday-gifts',
    footer: {
        text: "Gérer mes cadeaux",
        class: "text-primary"
    }
};

const cards = [...baseCards, birthdayGiftCard];
</script>

<template>
  <BaseContainer class="p-4">
    <div class="flex justify-between items-start flex-wrap gap-4">
      <div>
        <h1 class="text-2xl font-medium mb-2">
          Communiquez auprès de vos patients
        </h1>
        <p class="text-muted-foreground">
          Comment souhaitez-vous communiquer avec vos patients ?
        </p>
        <div class="flex mt-4 gap-4">
          <div v-if="credit" class="flex items-center gap-2">
            <div class="bg-white rounded-full p-2">
              <SendHorizonal class="size-4 text-primary" />
            </div>
            <span class="font-medium text-primary">{{ credit.balance }} crédits SMS disponibles</span>
          </div>
          <BuyCreditsButton />
        </div>
      </div>
      <router-link
        :to="{name: 'rcs'}"
        class="gradient flex gap-4 px-3 py-3 rounded-lg shadow-lg items-center hover:opacity-90 ml-auto"
      >
        <div class="size-12 bg-secondary rounded-lg flex items-center justify-center">
          <PartyPopper class="text-primary" />
        </div>
        <div class="text-white">
          <h3>Testez vous-même !</h3>
          <p class="text-sm text-secondary font-medium">
            Recevez un scénario RCS
          </p>
        </div>
        <ChevronRight class="text-secondary" />
      </router-link>
    </div>

    <UpcomingSequenceMessagesTimeline class="mt-4" />

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-4">
      <router-link
        v-for="(card, index) in cards"
        :key="index" :to="{name: card.routeName}"
        class="flex-1 basis-full sm:basis-[calc(50%-1rem)] lg:basis-[calc(33.333%-1.5rem)] min-w-[280px]"
      >
        <BaseCard
          :class="[
            'h-full border-[1px] border-transparent',
            'group transition-[transform,box-shadow] ease-out duration-200',
            'hover:shadow-xl hover:-translate-y-1',
            'hover:!border-primary cursor-pointer',
            card.border
          ]"
        >
          <BaseCardBody class="flex flex-col gap-4 h-full">
            <div class="flex gap-4 items-center">
              <div
                :class="[
                  'shrink-0 p-4 rounded-lg',
                  card.icon.background
                ]"
              >
                <component :is="card.icon.component" />
              </div>
              <span class="text-lg font-medium">{{ card.title }}</span>
            </div>
            <p class="text-muted-foreground flex-1">
              {{ card.description }}
            </p>
            <span class="text-primary font-medium">
              {{ card.footer.text }}
              <ArrowRight
                :class="[
                  'inline-flex size-4 font-medium transition-transform duration-200 group-hover:translate-x-1',
                  card.footer.class
                ]"
              />
            </span>
          </BaseCardBody>
        </BaseCard>
      </router-link>
    </div>
    <ApodisPremiumCreditUpsellBanner class="mt-4" />
  </BaseContainer>
</template>

<style scoped>

</style>
